import { TypedDocumentNode, gql } from '@apollo/client';
import {
  faCaretLeft,
  faCaretRight,
  faCircleInfo,
} from '@fortawesome/pro-solid-svg-icons';
import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useKey } from 'react-use';
import styled from 'styled-components';

// eslint-disable-next-line no-restricted-imports
import { ErrorBoundary } from '@sorare/error-boundary';

import {
  ProbabilisticBundleDesign,
  Rarity,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import IconButton from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import {
  HeadlineL,
  LabelL,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { messages } from '@sorare/core/src/components/rewards/RewardBoxDialog/utils';
import { ANY_SPORT_PLAY } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';

import { BoxOverviewDialog } from 'components/rewards/BoxOverviewDialog';
import { getFallbacks, useFormatBox } from 'components/rewards/RewardBoxDialog';

import {
  InventoryBoxesTypeQuery,
  InventoryBoxesTypeQueryVariables,
} from './__generated__/index.graphql';

const Hoverable = lazy(async () => import('@sorare/trois/src/atoms/Hoverable'));

const MouseCamera = lazy(
  async () => import('@sorare/trois/src/atoms/MouseCamera')
);
const Steps = lazy(async () => import('@sorare/trois/src/atoms/Steps'));

const Box = lazy(
  async () => import('@sorare/trois/src/components/ClaimRewardBoxes/Box')
);

const Factory = lazy(
  async () => import('@sorare/trois/src/components/Factory')
);

const INVENTORY_BOXES_TYPE_QUERY = gql`
  query InventoryBoxesTypeQuery($sport: Sport!) {
    currentUser {
      slug
      unopenedProbabilisticBundles(sport: $sport, first: 100) {
        nodes {
          id
          config {
            id
            design
            rarity
            division
            pictureUrl
            ...BoxOverviewDialog_probabilisticBundleConfig
          }
          so5Reward {
            slug
            so5Leaderboard {
              slug
              so5League {
                slug
                ...BoxOverviewDialog_so5League
              }
            }
          }
          ...useFormatBox_ProbabilisticBundle
        }
      }
    }
  }
  ${useFormatBox.fragments.ProbabilisticBundle}
  ${BoxOverviewDialog.fragments.probabilisticBundleConfig}
  ${BoxOverviewDialog.fragments.so5League}
` as TypedDocumentNode<
  InventoryBoxesTypeQuery,
  InventoryBoxesTypeQueryVariables
>;

const PageHeaderPortal = styled.div`
  padding: var(--unit);
`;
const Root = styled(Vertical)`
  height: 100%;
`;
const Content = styled.div`
  height: 100%;
  min-height: 0;
  flex: 1;
  position: relative;
`;
const StyledIconButton = styled(IconButton)`
  position: absolute;
  right: var(--double-unit);
  top: var(--double-unit);
  z-index: 1;
`;

type Props = {
  sport: Sport;
  type: string;
};

export const InventoryBoxesTypePage = ({ sport, type }: Props) => {
  const { formatMessage } = useIntl();
  const { generateSportContextPath } = useSportContext();
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const [oddsDialogOpen, setOddsDialogOpen] = useState(false);

  const { data } = useQuery(INVENTORY_BOXES_TYPE_QUERY, {
    variables: {
      sport,
    },
  });

  const { unopenedProbabilisticBundles } = data?.currentUser || {};
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const filteredBoxes =
    unopenedProbabilisticBundles?.nodes?.filter(box =>
      type && type in Rarity
        ? box.config.rarity === type &&
          box.config.design === ProbabilisticBundleDesign.PRO
        : box.config.design === ProbabilisticBundleDesign.RIVALS
    ) || [];

  // Do not update when unopenedProbabilisticBundles changes to keep opened boxes visible
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedBoxes = useMemo(() => filteredBoxes, [type]);

  const boxes = useFormatBox(selectedBoxes);

  useEffect(() => {
    setCurrentIndex(0);
  }, [type]);

  const prev = () => setCurrentIndex(i => Math.max(0, i - 1));
  const next = () =>
    setCurrentIndex(i => Math.min(selectedBoxes.length - 1, i + 1));
  useKey('ArrowLeft', prev, undefined, [prev]);
  useKey('ArrowRight', next, undefined, [next]);
  const [opening, setOpening] = useState<boolean>(false);

  useEffect(() => {
    setOpening(false);
  }, [currentIndex]);

  const fallbacks: React.JSX.Element[] = getFallbacks(
    selectedBoxes.flatMap(b => b.items)
  );

  const selectedBox = selectedBoxes.at(currentIndex);
  const selectedBoxNotOpened = filteredBoxes.find(
    b => b.id === selectedBox?.id
  );

  return (
    <>
      <PageHeader
        backTo={generateSportContextPath(ANY_SPORT_PLAY)}
        text={isDesktopAndAbove ? glossary.inventory : undefined}
        customTitle={
          !isDesktopAndAbove ? (
            <SBHorizontal>
              <Vertical gap={0}>
                <HeadlineL as="p">
                  <FormattedMessage {...glossary.inventory} />
                </HeadlineL>
                <LabelM as="p">
                  <FormattedMessage {...fantasy.boxes} />
                </LabelM>
              </Vertical>
              {selectedBox && (
                <StyledIconButton
                  color="transparent"
                  icon={faCircleInfo}
                  onClick={() => setOddsDialogOpen(true)}
                />
              )}
            </SBHorizontal>
          ) : undefined
        }
      />
      <ConditionalWrapper
        Wrapper={FullWidthContainer.NoPadding}
        wrap={isDesktopAndAbove}
      >
        <Root>
          {!isDesktopAndAbove && <PageHeaderPortal id="page-header-title" />}
          <Content>
            {selectedBox && isDesktopAndAbove && (
              <StyledIconButton
                color="transparent"
                icon={faCircleInfo}
                onClick={() => setOddsDialogOpen(true)}
              />
            )}
            <Suspense fallback={<LoadingIndicator fullScreen />}>
              <ErrorBoundary
                onCatch={() => {
                  // claim all boxes
                  boxes.forEach(b => b.onClaim());
                }}
                fallback={
                  <Vertical center gap={4}>
                    <HeadlineL as="h1">
                      <FormattedMessage {...messages.hereWhatYouWon} />
                    </HeadlineL>
                    <Horizontal gap={2} center>
                      {fallbacks.map((fallback, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index}>{fallback}</li>
                      ))}
                    </Horizontal>

                    <LabelS as="span">
                      <FormattedMessage {...messages.fallbackExplanation} />
                    </LabelS>
                  </Vertical>
                }
              >
                <>
                  <Factory context="box">
                    <Steps
                      distance={20}
                      steps={boxes.map(box => (
                        <Hoverable
                          key={box.id}
                          onClick={() => setOpening(true)}
                        >
                          <Box box={box} turnAround={false} />
                        </Hoverable>
                      ))}
                      currentIndex={currentIndex}
                    />
                    <MouseCamera disabled={opening} />
                  </Factory>
                  <IconButton
                    color="transparent"
                    onClick={prev}
                    disableDebounce
                    aria-label={formatMessage(glossary.previous)}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: 'var(--double-unit)',
                      transform: 'translate(-50%, -50%)',
                      display: currentIndex !== 0 ? 'block' : 'none',
                    }}
                  >
                    <FontAwesomeIcon icon={faCaretLeft} fontSize={64} />
                  </IconButton>
                  <IconButton
                    color="transparent"
                    onClick={next}
                    disableDebounce
                    aria-label={formatMessage(glossary.next)}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: 'var(--double-unit)',
                      transform: 'translate(-50%, -50%)',
                      display:
                        currentIndex < boxes.length - 1 ? 'block' : 'none',
                    }}
                  >
                    <FontAwesomeIcon icon={faCaretRight} fontSize={64} />
                  </IconButton>
                  {selectedBoxNotOpened && (
                    <LabelL
                      as="span"
                      color="var(--c-neutral-1000)"
                      bold
                      style={{
                        position: 'absolute',
                        bottom: 'var(--quadruple-unit)',
                        left: 0,
                        right: 0,
                        textAlign: 'center',
                        display: boxes.length ? 'initial' : 'none',
                      }}
                    >
                      <FormattedMessage {...messages.clickToOpenIt} />
                    </LabelL>
                  )}
                </>
              </ErrorBoundary>
            </Suspense>
          </Content>
        </Root>
        {oddsDialogOpen && selectedBox && (
          <BoxOverviewDialog
            open
            onClose={() => setOddsDialogOpen(false)}
            boxConfig={selectedBox.config}
            so5League={selectedBox.so5Reward?.so5Leaderboard.so5League}
          />
        )}
      </ConditionalWrapper>
    </>
  );
};

export default InventoryBoxesTypePage;
